import React, { useMemo } from 'react';
import { Box, Typography } from '@lucid/lego';
import type { CartItem } from '@lucid/cart';
import VehicleCard from '../VehicleCard/VehicleCard';
import styles from './VehicleList.module.scss';

const toGroupKey = (item: CartItem): string => {
  const ctx = item.itemContext as Record<string, string> | undefined;
  const model = ctx?.model ?? '';
  const trim = ctx?.trim ?? '';
  const year = ctx?.modelYear ?? ctx?.year ?? '';
  const color = ctx?.exteriorColor ?? '';
  if (model || trim || year || color) {
    return [model, trim, year, color].join('|');
  }
  return item.itemId ?? item.itemCode ?? 'unknown';
};

type ItemGroup = { groupId: string; items: CartItem[] };

const groupItems = (items: CartItem[]): ItemGroup[] => {
  const map = new Map<string, CartItem[]>();
  for (const item of items) {
    const key = toGroupKey(item);
    const existing = map.get(key);
    if (existing) {
      existing.push(item);
    } else {
      map.set(key, [item]);
    }
  }
  return Array.from(map.entries()).map(([groupId, groupItems]) => ({ groupId, items: groupItems }));
};

type VehicleListLabels = {
  emptyCart?: string;
};

export type VehicleListProps = {
  theme: 'dark' | 'light';
  labels?: VehicleListLabels;
  isLoading: boolean;
  items: CartItem[];
  currencyCode?: string;
  expandedItems: Record<string, boolean>;
  onToggle: (id: string) => void;
  customerId: string;
  onAllItemsDeleted?: () => void;
};

/**
 * Renders the list of vehicle cards in the cart, including loading and empty states.
 * @component
 * @param {VehicleListProps} props
 * @returns {JSX.Element}
 * @example
 * <VehicleList theme="light" isLoading={false} items={items} currencyCode="USD" expandedItems={{}} onToggle={() => {}} customerId="123" />
 */
const VehicleList: React.FC<VehicleListProps> = ({
  theme,
  labels,
  isLoading,
  items,
  currencyCode,
  expandedItems,
  onToggle,
  customerId,
  onAllItemsDeleted,
}) => {
  const groups = useMemo(() => groupItems(items), [items]);

  return (
    <Box
      className={styles.vehicleList}
      data-testid="checkout-vehicle-list"
      background="white"
      pl={16}
      pr={16}
      pb={24}
    >
      {isLoading && (
        <Box pt={24} pb={24}>
          <Typography variant="body" size="small" color="gray-3" theme={theme}>
            Loading...
          </Typography>
        </Box>
      )}
      {!isLoading && items.length === 0 && (
        <Box pt={24} pb={24}>
          <Typography
            variant="body"
            size="small"
            color="gray-3"
            theme={theme}
            data-testid="checkout-empty-state"
          >
            {labels?.emptyCart ?? 'Your cart is empty.'}
          </Typography>
        </Box>
      )}
      {groups.map(({ groupId, items: groupItems }) => (
        <VehicleCard
          key={groupId}
          items={groupItems}
          currencyCode={currencyCode}
          theme={theme}
          isExpanded={expandedItems[groupId] ?? false}
          onToggle={() => onToggle(groupId)}
          customerId={customerId}
          onAllItemsDeleted={onAllItemsDeleted}
        />
      ))}
    </Box>
  );
};

export default VehicleList;
